"use client";

import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import SortableProductImageItem from './SortableProductImageItem';
import { Loader2, Plus } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';

interface ImageStageProps {
  currentImages: string[];
  onDragEnd: (event: DragEndEvent) => void;
  onRemoveImage: (url: string) => void;
  disabled: boolean;
  isUploading: boolean;
  onOpenGallery?: () => void;
}

const ImageStage: React.FC<ImageStageProps> = ({
  currentImages,
  onDragEnd,
  onRemoveImage,
  disabled,
  isUploading,
  onOpenGallery,
}) => {
  // Dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Función dummy para mantener la interfaz de SortableProductImageItem
  const handleSetMainImageDummy = () => {
    // La imagen principal es siempre la primera, el DND se encarga del reordenamiento.
  };

  if (currentImages.length === 0) {
    // Permitir drag & drop de archivos
    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      // Subir archivos arrastrados si existen
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length > 0) {
        // Disparar un evento personalizado para que el padre los maneje
        const customEvent = new CustomEvent('image-drop', { detail: { files } });
        window.dispatchEvent(customEvent);
      }
      if (onOpenGallery) onOpenGallery();
    };
    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
    };
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => onOpenGallery && onOpenGallery()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpenGallery && onOpenGallery(); }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="w-full h-24 flex items-center justify-center border-2 border-dashed rounded-md text-muted-foreground cursor-pointer"
      >
        Haz click para seleccionar las imágenes de este producto
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={currentImages}
        strategy={rectSortingStrategy}
      >
        <div className="w-full space-y-2">
          <ScrollArea className="w-full whitespace-nowrap rounded-md border pb-4">
            <div className="flex flex-wrap gap-2 p-4 min-w-full">
              {currentImages.map((url, index) => (
                <div key={url} className="flex-shrink-0 w-[96px]">
                  <SortableProductImageItem
                    url={url}
                    index={index}
                    isMainImage={index === 0} // La primera es siempre la principal
                    onSetMainImage={handleSetMainImageDummy}
                    onRemove={onRemoveImage}
                    disabled={disabled || isUploading}
                  />
                </div>
              ))}
            </div>
            <ScrollBar orientation="horizontal" className="hidden" />
          </ScrollArea>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenGallery && onOpenGallery()}
            disabled={disabled || isUploading}
            className="w-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            Agregar más imágenes
          </Button>
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default ImageStage;